/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Routing sheet (F14): one wrapped DK per recipient (+ author + optional
 * system identity), each entry binding to the recipient's identity-id and
 * signing-key fingerprint via AAD, the whole sheet signed by the author's
 * ML-DSA-65 key.
 *
 * Three structural integrity guarantees:
 *
 *   1. Author can always read. The author is wrapped *into* the sheet
 *      structurally (named parameter, not hidden inside `recipients`),
 *      so the caller cannot forget to wrap-to-self.
 *   2. Recipient swap fails. Each entry's AAD binds to the recipient's
 *      identity-id and signing-key fingerprint, so moving entry A into
 *      entry B's slot fails the AEAD tag check at unwrap.
 *   3. Sheet integrity. The full sheet is signed with ML-DSA-65 over a
 *      canonical encoding (`buildSheetSigningMessage` sorts entries by
 *      `identityId` byte order at both sign- and verify-time), so a
 *      tampered sheet — adding entries, removing entries, swapping
 *      *content* across entries, or altering author metadata — fails
 *      the signature. **Pure transport-order permutation does *not*
 *      fail verification** — the canonical sort is deliberate so the
 *      wire format is stable regardless of how a caller built the
 *      sheet. To enforce "what I sent is what I signed" on the
 *      attacker's slot-swap path, the per-entry AEAD AAD binds to the
 *      recipient's identity-id and fingerprint (#110).
 *
 * Crypto:
 *   - ML-KEM-768 encapsulate to recipient → 32-byte sharedSecret + 1088-byte ciphertext
 *   - HKDF-SHA-256 (empty salt, info = utf8('BD-routing-v1')) → 32-byte KEK
 *   - XChaCha20-Poly1305 (24-byte nonce, AAD-bound) wraps the DK
 *   - ML-DSA-65 signs the canonical-encoded sheet
 *
 * See `docs/Application_redesign.md` §3 stage 5 and
 * `docs/plan-crypto-redesign.md` §F14.
 */

import { z } from 'zod';
import { base64nopad } from '@scure/base';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  encrypt as aeadEncrypt,
  decrypt as aeadDecrypt,
} from '../crypto/aead.js';
import {
  encapsulate as kemEncapsulate,
  decapsulate as kemDecapsulate,
} from '../crypto/mlkem.js';
import { sign as mldsaSign, verify as mldsaVerify } from '../crypto/mldsa.js';
import { defaultRandom, type Random } from '../random.js';
import { FINGERPRINT_LENGTH } from '../keys/fingerprint.js';

/** Locked algorithm identifiers. */
export const SHEET_ALGORITHM_ID = 'ML-KEM-768+XChaCha20-Poly1305' as const;
export const SHEET_SIGNING_ALGORITHM = 'ML-DSA-65' as const;

/** Wire-format version (bumps require a new parser; old never gets re-read). */
export const SHEET_VERSION = 1;

/** XChaCha20-Poly1305 sizes — copied as constants to avoid a wider crypto-export surface. */
const KEK_LENGTH_BYTES = 32;
const NONCE_LENGTH_BYTES = 24;

/** HKDF info string — versioned. */
const HKDF_INFO = new TextEncoder().encode('BD-routing-v1');

/** Magic bytes + domain bytes for the typed-field byte concatenations. */
const ENTRY_AAD_MAGIC = new Uint8Array([0x42, 0x44, 0x41, 0x31]); // "BDA1"
const SHEET_SIG_MAGIC = new Uint8Array([0x42, 0x44, 0x52, 0x31]); // "BDR1"
const DOMAIN_ROUTING_ENTRY = 0x10;
const DOMAIN_ROUTING_SHEET = 0x20;

/* --- Inputs (in-memory shapes for callers) --- */

export interface RoutingSheetRecipient {
  /** Identity id (typically email or uuid). */
  identityId: string;
  /** ML-KEM-768 public key (1184 bytes), from a verified F12 bundle. */
  kemPublicKey: Uint8Array;
  /** SHA-256 of the recipient's signing public key — the F12 TOFU pin. */
  signingKeyFingerprint: Uint8Array;
}

export interface RoutingSheetAuthor extends RoutingSheetRecipient {
  /** ML-DSA-65 private key (4032 bytes), used to sign the sheet. */
  signingPrivateKey: Uint8Array;
}

/* --- Errors --- */

export class RoutingSheetSignatureError extends Error {
  readonly reason: 'invalid-signature' | 'invalid-shape' | 'invalid-algorithm';
  constructor(message: string, reason: RoutingSheetSignatureError['reason']) {
    super(message);
    this.name = 'RoutingSheetSignatureError';
    this.reason = reason;
  }
}

export class RoutingSheetUnwrapError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'RoutingSheetUnwrapError';
  }
}

export class RecipientNotFoundError extends Error {
  readonly identityId: string;
  constructor(identityId: string) {
    super(`recipient '${identityId}' not in routing sheet`);
    this.name = 'RecipientNotFoundError';
    this.identityId = identityId;
  }
}

/**
 * Thrown by `buildRoutingSheet` when two entries in the union of
 * `[author, ...recipients, systemIdentity]` share an `identityId`.
 * The duplicate may be a caller bug (author placed in `recipients`
 * by mistake, two recipient stubs with the same email) or a sign of
 * a misconfigured `systemIdentity`. Either way the sheet is
 * ambiguous: `unwrapForRecipient` finds the first match by sort
 * order, so the second entry is dead weight and the
 * author-can-read property test can pass structurally even when
 * the sheet is wrong. Throwing here surfaces the misuse at build
 * time rather than letting it propagate (#109).
 */
export class DuplicateRecipientError extends Error {
  readonly identityId: string;
  constructor(identityId: string) {
    super(`duplicate identityId in routing sheet: '${identityId}'`);
    this.name = 'DuplicateRecipientError';
    this.identityId = identityId;
  }
}

/**
 * Thrown when a byte sequence violates a fixed-size or length-prefix
 * invariant of the routing-sheet canonical encoding:
 *
 *   - `length-prefix-overflow-u8`  — input longer than 255 bytes for a
 *     1-byte length prefix. Without the guard, `length & 0xff` wraps
 *     silently and two decoders disagree on where the field ends —
 *     producing signature mismatches at best, parser confusion at
 *     worst when sheets cross trust boundaries (#107).
 *   - `length-prefix-overflow-u16` — same, for 2-byte length prefixes
 *     (limit 65_535) (#107).
 *   - `fingerprint-wrong-length`   — fingerprint not exactly the
 *     SHA-256-derived `FINGERPRINT_LENGTH` (32). The previous
 *     `length > 0xff` check let any length 0..255 through, weakening
 *     the binding-to-identity guarantee (#108).
 */
export class RoutingSheetEncodingError extends Error {
  readonly reason:
    | 'length-prefix-overflow-u8'
    | 'length-prefix-overflow-u16'
    | 'fingerprint-wrong-length';
  constructor(message: string, reason: RoutingSheetEncodingError['reason']) {
    super(message);
    this.name = 'RoutingSheetEncodingError';
    this.reason = reason;
  }
}

/* --- Zod wire schema (mirrors F12 lookup.ts) --- */

const base64nopadBytes = z.string().transform((s, ctx) => {
  try {
    return base64nopad.decode(s);
  } catch {
    ctx.addIssue({ code: 'custom', message: 'invalid base64nopad encoding' });
    return z.NEVER;
  }
});

// #108: fingerprints are SHA-256 over the signing key, so they're
// always exactly `FINGERPRINT_LENGTH` (32) bytes. Tighten the schema
// from "non-empty bytes" to "exactly 32 bytes" so a malformed sheet
// is rejected at parse time rather than slipping through to the AAD
// layer.
const fingerprintBytes = base64nopadBytes.refine(
  bytes => bytes.length === FINGERPRINT_LENGTH,
  {
    // Zod v4: dynamic messages live on `params.error`; the v3-style
    // second-arg function returning `{ message }` is silently ignored
    // and falls back to the default "Invalid input".
    error: issue =>
      `fingerprint must be exactly ${FINGERPRINT_LENGTH} bytes; got ${(issue.input as Uint8Array).length}`,
  }
);

export const RoutingSheetEntrySchema = z
  .object({
    identityId: z.string().min(1),
    algorithmId: z.literal(SHEET_ALGORITHM_ID),
    kemCiphertext: base64nopadBytes,
    wrappedDk: base64nopadBytes,
    nonce: base64nopadBytes,
    recipientFingerprint: fingerprintBytes,
    aad: base64nopadBytes,
  })
  .strict();

export type RoutingSheetEntry = z.infer<typeof RoutingSheetEntrySchema>;

export const RoutingSheetSchema = z
  .object({
    version: z.literal(SHEET_VERSION),
    documentId: z.string().min(1),
    algorithmId: z.literal(SHEET_ALGORITHM_ID),
    authorId: z.string().min(1),
    authorFingerprint: fingerprintBytes,
    entries: z.array(RoutingSheetEntrySchema),
    signature: base64nopadBytes,
    signingAlgorithm: z.literal(SHEET_SIGNING_ALGORITHM),
  })
  .strict();

export type RoutingSheet = z.infer<typeof RoutingSheetSchema>;

/**
 * Parse an unknown wire payload into a typed `RoutingSheet`. Throws
 * `RoutingSheetSignatureError(reason: 'invalid-shape')` for any Zod
 * failure (missing/extra fields, malformed base64).
 */
export const parseRoutingSheet = (raw: unknown): RoutingSheet => {
  const result = RoutingSheetSchema.safeParse(raw);
  if (!result.success) {
    throw new RoutingSheetSignatureError(
      `routing sheet shape rejected: ${result.error.message}`,
      'invalid-shape'
    );
  }
  return result.data;
};

/* --- byte helpers (mirror src/services/vault/aad.ts) --- */

const utf8 = new TextEncoder();

/**
 * Compare two strings as their UTF-8 byte sequences — the canonical
 * ordering used at sign- and verify-time. JS `<` / `>` on strings
 * sorts UTF-16 code units, which agrees with UTF-8 for ASCII but
 * diverges for code points above U+007F. The biggest divergence is
 * at the BMP / supplementary boundary: surrogate-paired emoji
 * (U+10000+, UTF-16 lead 0xD800-0xDBFF, UTF-8 lead byte 0xF0+) sort
 * *before* high BMP chars (U+E000-U+FFFF, UTF-16 0xE000+, UTF-8 lead
 * byte 0xEE-0xEF) in UTF-16 — but *after* them in UTF-8. A backend
 * doing real UTF-8 lexicographic order would canonicalise the
 * routing sheet differently, fail signature verification, and the
 * source of the mismatch would be invisible at every layer above
 * the byte comparison (#106).
 *
 * Returns negative if a's UTF-8 bytes sort before b's, positive if
 * after, zero if equal — the standard `Array.sort` comparator shape.
 */
const compareUtf8Bytes = (a: string, b: string): number => {
  const ka = utf8.encode(a);
  const kb = utf8.encode(b);
  const min = Math.min(ka.length, kb.length);
  for (let i = 0; i < min; i++) {
    if (ka[i] !== kb[i]) return (ka[i] as number) - (kb[i] as number);
  }
  return ka.length - kb.length;
};

const u8 = (n: number): Uint8Array => Uint8Array.of(n & 0xff);

const u16be = (n: number): Uint8Array =>
  Uint8Array.of((n >>> 8) & 0xff, n & 0xff);

const u32be = (n: number): Uint8Array =>
  Uint8Array.of(
    (n >>> 24) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 8) & 0xff,
    n & 0xff
  );

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
};

const U8_MAX = 0xff;
const U16_MAX = 0xffff;

const lenPrefix8 = (b: Uint8Array): Uint8Array => {
  // #107: refuse to silently wrap `length & 0xff`. A serialiser
  // returning `[len_low_byte, ...payload]` for a 300-byte input
  // would emit `[0x2c, ...300 bytes]`, which a strict decoder
  // reading 0x2c bytes (44) leaves with 256 trailing bytes of
  // garbage — signature mismatch, or worse, parser confusion when
  // the canonical encoding crosses trust boundaries.
  if (b.length > U8_MAX) {
    throw new RoutingSheetEncodingError(
      `lenPrefix8: field length ${b.length} exceeds u8 cap ${U8_MAX}`,
      'length-prefix-overflow-u8'
    );
  }
  return concat(u8(b.length), b);
};

const lenPrefix16 = (b: Uint8Array): Uint8Array => {
  if (b.length > U16_MAX) {
    throw new RoutingSheetEncodingError(
      `lenPrefix16: field length ${b.length} exceeds u16 cap ${U16_MAX}`,
      'length-prefix-overflow-u16'
    );
  }
  return concat(u16be(b.length), b);
};

/**
 * Build the per-entry AAD bytes. Bound to the recipient's identity-id +
 * fingerprint + the document context, so an attacker who swaps two
 * entries fails the AEAD tag (the AAD won't reconstruct the same way
 * under the new slot's identity).
 *
 * Layout:
 *   "BDA1"                                (4 bytes magic)
 *   u16be(SHEET_VERSION = 1)              (2 bytes)
 *   lenPrefix16(utf8(documentId))         (2 + N, N ≤ 65_535)
 *   lenPrefix16(utf8(identityId))         (2 + N, N ≤ 65_535)
 *   lenPrefix8(fingerprint)               (1 + exactly FINGERPRINT_LENGTH)
 *   lenPrefix8(utf8(SHEET_ALGORITHM_ID))  (1 + ~28)
 *   u8(DOMAIN_ROUTING_ENTRY = 0x10)       (1 byte)
 *
 * Length-overflow checks for `documentId` / `identityId` / `algorithmId`
 * are baked into `lenPrefix8` / `lenPrefix16` themselves (#107) — every
 * caller benefits, no per-field duplication. The fingerprint check is
 * stricter than the helper bound (must equal `FINGERPRINT_LENGTH`, not
 * just ≤ 255) so it stays inline (#108).
 */
export const buildEntryAad = (params: {
  documentId: string;
  identityId: string;
  fingerprint: Uint8Array;
}): Uint8Array => {
  const { documentId, identityId, fingerprint } = params;
  if (documentId.length === 0) throw new Error('documentId must be non-empty');
  if (identityId.length === 0) throw new Error('identityId must be non-empty');
  if (fingerprint.length !== FINGERPRINT_LENGTH) {
    throw new RoutingSheetEncodingError(
      `fingerprint must be exactly ${FINGERPRINT_LENGTH} bytes; got ${fingerprint.length}`,
      'fingerprint-wrong-length'
    );
  }
  const docBytes = utf8.encode(documentId);
  const idBytes = utf8.encode(identityId);
  const algoBytes = utf8.encode(SHEET_ALGORITHM_ID);
  return concat(
    ENTRY_AAD_MAGIC,
    u16be(SHEET_VERSION),
    lenPrefix16(docBytes),
    lenPrefix16(idBytes),
    lenPrefix8(fingerprint),
    lenPrefix8(algoBytes),
    u8(DOMAIN_ROUTING_ENTRY)
  );
};

/**
 * Build the deterministic byte sequence the routing-sheet signature
 * covers. Sorted entries by `identityId` UTF-8 byte order.
 *
 * Layout:
 *   "BDR1"
 *   u16be(SHEET_VERSION)
 *   lenPrefix16(utf8(documentId))
 *   lenPrefix16(utf8(authorId))
 *   lenPrefix8(authorFingerprint)
 *   u32be(entries.length)
 *   concat(entries.sortByIdentityId().map(serializeEntry))
 *   u8(DOMAIN_ROUTING_SHEET = 0x20)
 *
 * `serializeEntry` is also a typed-field concat so every byte of every
 * entry is signed.
 */
export const buildSheetSigningMessage = (
  sheet: Pick<
    RoutingSheet,
    'documentId' | 'authorId' | 'authorFingerprint' | 'entries' | 'algorithmId'
  >
): Uint8Array => {
  if (sheet.algorithmId !== SHEET_ALGORITHM_ID) {
    throw new RoutingSheetSignatureError(
      `unsupported sheet algorithm: ${String(sheet.algorithmId)}`,
      'invalid-algorithm'
    );
  }
  const sortedEntries = [...sheet.entries].sort((a, b) =>
    compareUtf8Bytes(a.identityId, b.identityId)
  );
  return concat(
    SHEET_SIG_MAGIC,
    u16be(SHEET_VERSION),
    lenPrefix16(utf8.encode(sheet.documentId)),
    lenPrefix16(utf8.encode(sheet.authorId)),
    lenPrefix8(sheet.authorFingerprint),
    u32be(sortedEntries.length),
    ...sortedEntries.map(serializeEntryForSignature),
    u8(DOMAIN_ROUTING_SHEET)
  );
};

const serializeEntryForSignature = (entry: RoutingSheetEntry): Uint8Array =>
  concat(
    lenPrefix16(utf8.encode(entry.identityId)),
    lenPrefix8(utf8.encode(entry.algorithmId)),
    lenPrefix16(entry.kemCiphertext),
    lenPrefix16(entry.wrappedDk),
    lenPrefix8(entry.nonce),
    lenPrefix8(entry.recipientFingerprint),
    lenPrefix16(entry.aad)
  );

/**
 * KEM-then-wrap a single recipient: encapsulate to their KEM public key,
 * derive a KEK via HKDF-SHA-256, wrap the DK under XChaCha20-Poly1305
 * with AAD bound to (documentId, identityId, fingerprint, algorithmId).
 */
const wrapForRecipient = (params: {
  recipient: RoutingSheetRecipient;
  documentId: string;
  dk: Uint8Array;
  rng: Random;
}): RoutingSheetEntry => {
  const { recipient, documentId, dk, rng } = params;
  const { sharedSecret, ciphertext: kemCiphertext } = kemEncapsulate(
    rng,
    recipient.kemPublicKey
  );
  const kek = hkdf(
    sha256,
    sharedSecret,
    undefined,
    HKDF_INFO,
    KEK_LENGTH_BYTES
  );
  const aad = buildEntryAad({
    documentId,
    identityId: recipient.identityId,
    fingerprint: recipient.signingKeyFingerprint,
  });
  const nonce = rng.getRandomBytes(NONCE_LENGTH_BYTES);
  const wrappedDk = aeadEncrypt({ key: kek, nonce, plaintext: dk, aad });
  return {
    identityId: recipient.identityId,
    algorithmId: SHEET_ALGORITHM_ID,
    kemCiphertext,
    wrappedDk,
    nonce,
    recipientFingerprint: recipient.signingKeyFingerprint,
    aad,
  };
};

/**
 * Build a routing sheet wrapping `dk` for `author`, every recipient,
 * and (optionally) the system identity. The author is always included
 * structurally — they cannot decrypt without an entry, and the named
 * parameter prevents the caller from forgetting.
 *
 * Entries are sorted by `identityId` (UTF-8 byte order) for
 * determinism, then the whole sheet is signed with the author's
 * ML-DSA-65 private key.
 */
export const buildRoutingSheet = (params: {
  documentId: string;
  dk: Uint8Array;
  author: RoutingSheetAuthor;
  recipients: readonly RoutingSheetRecipient[];
  systemIdentity?: RoutingSheetRecipient;
  rng?: Random;
}): RoutingSheet => {
  const {
    documentId,
    dk,
    author,
    recipients,
    systemIdentity,
    rng = defaultRandom,
  } = params;
  if (documentId.length === 0) throw new Error('documentId must be non-empty');
  if (dk.length !== KEK_LENGTH_BYTES) {
    throw new Error(`dk must be ${KEK_LENGTH_BYTES} bytes`);
  }

  const allRecipients: RoutingSheetRecipient[] = [
    author,
    ...recipients,
    ...(systemIdentity ? [systemIdentity] : []),
  ];

  // Reject duplicates *before* doing per-entry ML-KEM encapsulation
  // work — both to save the work and to give the caller a clear,
  // typed error rather than a sheet with dead entries. See
  // `DuplicateRecipientError` for the failure modes this catches.
  const seenIds = new Set<string>();
  for (const r of allRecipients) {
    if (seenIds.has(r.identityId)) {
      throw new DuplicateRecipientError(r.identityId);
    }
    seenIds.add(r.identityId);
  }

  const entries = allRecipients
    .map(recipient => wrapForRecipient({ recipient, documentId, dk, rng }))
    .sort((a, b) => compareUtf8Bytes(a.identityId, b.identityId));

  const partial = {
    documentId,
    authorId: author.identityId,
    authorFingerprint: author.signingKeyFingerprint,
    entries,
    algorithmId: SHEET_ALGORITHM_ID,
  };
  const signature = mldsaSign(
    author.signingPrivateKey,
    buildSheetSigningMessage(partial)
  );

  return {
    version: SHEET_VERSION,
    documentId,
    algorithmId: SHEET_ALGORITHM_ID,
    authorId: author.identityId,
    authorFingerprint: author.signingKeyFingerprint,
    entries,
    signature,
    signingAlgorithm: SHEET_SIGNING_ALGORITHM,
  };
};

/**
 * Verify the routing sheet's ML-DSA-65 signature against the supplied
 * author signing public key. Returns the sheet on success; throws
 * `RoutingSheetSignatureError` on failure.
 */
export const verifyRoutingSheetSignature = (
  sheet: RoutingSheet,
  authorSigningPublicKey: Uint8Array
): RoutingSheet => {
  if (sheet.signingAlgorithm !== SHEET_SIGNING_ALGORITHM) {
    throw new RoutingSheetSignatureError(
      `unsupported signing algorithm: ${String(sheet.signingAlgorithm)}`,
      'invalid-algorithm'
    );
  }
  const ok = mldsaVerify(
    authorSigningPublicKey,
    buildSheetSigningMessage(sheet),
    sheet.signature
  );
  if (!ok) {
    throw new RoutingSheetSignatureError(
      `routing sheet signature does not verify for author '${sheet.authorId}'`,
      'invalid-signature'
    );
  }
  return sheet;
};

/**
 * Find the entry for `identityId` and unwrap the DK with the
 * recipient's KEM private key. The recipient must also pass the
 * fingerprint they expect their own signing key to have — this is
 * compared against `entry.recipientFingerprint` *and* AAD-bound at
 * encrypt time, so a mismatch surfaces as either a deterministic
 * fingerprint check or an AEAD tag failure.
 *
 * @throws {RecipientNotFoundError} if `identityId` isn't in the sheet.
 * @throws {RoutingSheetUnwrapError} if the fingerprint doesn't match
 *   the entry, the entry has been moved to another slot, or the
 *   wrapped DK fails the AEAD tag check.
 */
export const unwrapForRecipient = (params: {
  sheet: RoutingSheet;
  identityId: string;
  kemPrivateKey: Uint8Array;
  expectedFingerprint: Uint8Array;
}): Uint8Array => {
  const { sheet, identityId, kemPrivateKey, expectedFingerprint } = params;
  const entry = sheet.entries.find(e => e.identityId === identityId);
  if (!entry) {
    throw new RecipientNotFoundError(identityId);
  }
  if (!fingerprintsEqual(entry.recipientFingerprint, expectedFingerprint)) {
    throw new RoutingSheetUnwrapError(
      `entry for '${identityId}' has a different fingerprint than expected`
    );
  }
  const sharedSecret = kemDecapsulate(kemPrivateKey, entry.kemCiphertext);
  const kek = hkdf(
    sha256,
    sharedSecret,
    undefined,
    HKDF_INFO,
    KEK_LENGTH_BYTES
  );
  // Reconstruct AAD locally rather than trusting `entry.aad` — that's
  // what the signature covers, but unwrap binds to the canonical
  // reconstruction so a mutated `entry.aad` is detected too.
  const expectedAad = buildEntryAad({
    documentId: sheet.documentId,
    identityId,
    fingerprint: expectedFingerprint,
  });
  if (!bytesEqual(expectedAad, entry.aad)) {
    throw new RoutingSheetUnwrapError(
      `entry AAD for '${identityId}' does not match the canonical reconstruction`
    );
  }
  try {
    return aeadDecrypt({
      key: kek,
      nonce: entry.nonce,
      ciphertext: entry.wrappedDk,
      aad: expectedAad,
    });
  } catch (err) {
    throw new RoutingSheetUnwrapError(
      `wrapped-DK decrypt failed for '${identityId}': ${
        err instanceof Error ? err.message : String(err)
      }`,
      { cause: err }
    );
  }
};

const fingerprintsEqual = (a: Uint8Array, b: Uint8Array): boolean =>
  bytesEqual(a, b);

const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= (a[i] as number) ^ (b[i] as number);
  }
  return diff === 0;
};
